import { useEffect, useState } from 'react';
import { settingsService } from '../../../../../services/settingsService';

/**
 * useReceiptExtraInfoFields
 *
 * Leser de profil-konfigurerte tilleggsinfo-feltene for innlogget operatør i
 * varemottak. Verdiene er navn på felt fra innkjøpsordrelinjen som skal vises
 * som tilleggsinfo (se receiptExtraInfo-resolveren).
 *
 * Bruker getEffectiveSetting slik at hierarkiet Profile -> System -> Default
 * resolves automatisk (samme mønster som useBarcodeScannerEnabled og
 * ModernPickingApp). Backend bruker TenantContext.OperatorId - ingen userId
 * sendes fra frontend.
 *
 * @returns liste med konfigurerte felt-navn (tomme/uoppsatte er filtrert bort),
 *          f.eks. ['OrderLineFreeField1', 'OrderLineFreeField2'].
 */
const SETTING_KEYS = [
  'receipt.extraInfoField1',
  'receipt.extraInfoField2',
  'receipt.extraInfoField3',
];

export function useReceiptExtraInfoFields(): string[] {
  const slots = useReceiptExtraInfoSlots();
  // Bevar bakoverkompatibilitet: returner kun de slottene som er konfigurert.
  return slots.filter((v) => v.length > 0);
}

/**
 * useReceiptExtraInfoSlots
 *
 * Som useReceiptExtraInfoFields, men returnerer feltnavnene posisjonelt (én
 * verdi per slot 1/2/3, tom streng der slottet ikke er konfigurert). Brukes der
 * slottene skal vises på ulike steder i UI - f.eks. felt 1 ved siden av
 * linjenummeret og felt 2/3 under beskrivelsen i listevisninger.
 *
 * @returns alltid en array med tre elementer: [field1, field2, field3].
 */
export function useReceiptExtraInfoSlots(): string[] {
  const [slots, setSlots] = useState<string[]>(['', '', '']);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const results = await Promise.all(
          SETTING_KEYS.map((key) =>
            settingsService.getEffectiveSetting(key).catch(() => null)
          )
        );
        if (cancelled) return;
        const names = results.map(
          (r) => r?.currentValue?.toString().trim() || ''
        );
        setSlots(names);
      } catch {
        if (!cancelled) setSlots(['', '', '']);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return slots;
}
